import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import PrivateRoute from '../components/PrivateRoute';
import AdminRoute from '../components/admin/AdminRoute';
import { useAuth } from '../context/AuthContext';
import FullScreenLoader from '../components/common/FullScreenLoader';

// Page imports
import AuthPage from './AuthPage';
import LandingPage from './LandingPage';
import Dashboard from './Dashboard';
import SettingsPage from './SettingsPages';
import RechargeCredits from './RechargeCredits';
import SuccessPage from './SuccessPage';
import MuzoUpscalePage from './MuzoUpscalePage';
import ImageGenerationPage from './ImageGenerationPage';
import AdminDashboard from './AdminDashboard';
import LegalHub from './legal/LegalHub';
import CGUPage from './legal/CGUPage';
import MentionsLegalesPage from './legal/MentionsLegalesPage';
import CGVPage from './legal/CGVPage';
import PrivacyPolicyPage from './legal/PrivacyPolicyPage';

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <FullScreenLoader message="Chargement..." />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Route configuration
const ROUTES = {
  // Public routes
  PUBLIC: [
    {
      path: '/',
      element: <LandingPage />,
    },
    {
      path: '/:lang',
      element: <LandingPage />,
    },
    {
      path: '/sora',
      element: <LandingPage />,
    },
    {
      path: '/login',
      element: (
        <PublicRoute>
          <AuthPage />
        </PublicRoute>
      ),
    },
    {
      path: '/signup',
      element: (
        <PublicRoute>
          <AuthPage />
        </PublicRoute>
      ),
    },
    {
      path: '/legal',
      element: <LegalHub />,
    },
    {
      path: '/legal/cgu',
      element: <CGUPage />,
    },
    {
      path: '/legal/mentions-legales',
      element: <MentionsLegalesPage />,
    },
    {
      path: '/legal/cgv',
      element: <CGVPage />,
    },
    {
      path: '/legal/privacy',
      element: <PrivacyPolicyPage />,
    },
    {
      path: '/register',
      element: (
        <PublicRoute>
          <AuthPage />
        </PublicRoute>
      ),
    },
    {
      path: '/reset-password',
      element: (
        <PublicRoute>
          <AuthPage />
        </PublicRoute>
      ),
    },
    {
      path: '/success',
      element: <SuccessPage />,
    },
  ],
  
  // Private routes (require authentication and layout)
  PRIVATE: [
    {
      path: '/dashboard',
      element: <Dashboard />,
    },
    {
      path: '/upscale',
      element: <MuzoUpscalePage />,
    },
    {
      path: '/settings',
      element: <SettingsPage />,
    },
    {
      path: '/credits',
      element: <RechargeCredits />,
    },
    {
      path: '/recharge-credits',
      element: <RechargeCredits />,
    },
    // Keep backward compatibility: redirect /credit-history to settings?tab=usage
    {
      path: '/credit-history',
      element: <Navigate to="/settings?tab=usage" replace />,
    },
  ],
  
  // Admin routes (require admin privileges)
  ADMIN: [
    {
      path: '/admin',
      element: <AdminDashboard />,
    },
    {
      path: '/image-generation',
      element: <ImageGenerationPage />,
    },
  ],
};

const AppRoutes = () => {
  return (
    <Routes>
      {/* Public Routes */}
      {ROUTES.PUBLIC.map(({ path, element }) => (
        <Route key={path} path={path} element={element} />
      ))}

      {/* Private Routes - All wrapped with PrivateRoute and Layout */}
      {ROUTES.PRIVATE.map(({ path, element }) => (
        <Route
          key={path}
          path={path}
          element={
            <Layout>
              <PrivateRoute>
                {element}
              </PrivateRoute>
            </Layout>
          }
        />
      ))}

      {/* Admin Routes - Wrapped with AdminRoute and Layout */}
      {ROUTES.ADMIN.map(({ path, element }) => (
        <Route
          key={path}
          path={path}
          element={
            <Layout>
              <AdminRoute>
                {element}
              </AdminRoute>
            </Layout>
          }
        />
      ))}

      {/* Fallback route - redirect to dashboard if authenticated, login if not */}
      <Route
        path="*"
        element={
          <PrivateRoute>
            <Navigate to="/dashboard" replace />
          </PrivateRoute>
        }
      />
    </Routes>
  );
};

export default AppRoutes;
